// A door's swing in the plan (docs/claude/rooms.md, *Doors*): the ONE value every
// kind of door gives — a room's door, a drawn wall's door, a glazing's door —
// and the two places it is drawn, the canvas and the DXF. The arc runs from the
// open leaf's tip back to the closed leaf's free end, the short way round.
import type { DxfEntity } from '../shared/dxf';
import type { Vec2 } from '../shared/types';

/** Where a door hinges, where its leaf's free end is open (square to the wall)
 *  and closed (on the far jamb), and the leaf's length. */
export interface DoorSwing {
  hinge: Vec2;
  open: Vec2;
  closed: Vec2;
  radius: number;
}

/** The swing with its points mapped (a room's local frame to the world's). */
export function mapSwing(s: DoorSwing, f: (p: Vec2) => Vec2): DoorSwing {
  return { hinge: f(s.hinge), open: f(s.open), closed: f(s.closed), radius: s.radius };
}

/** The arc's start angle and its signed sweep (radians, |sweep| ≤ π). */
export function swingSweep(s: DoorSwing): { a0: number; sweep: number } {
  const a0 = Math.atan2(s.open.y - s.hinge.y, s.open.x - s.hinge.x);
  let sweep = Math.atan2(s.closed.y - s.hinge.y, s.closed.x - s.hinge.x) - a0;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep < -Math.PI) sweep += 2 * Math.PI;
  return { a0, sweep };
}

/** The arc as `segs + 1` points from the open tip to the closed end. */
export function swingArc(s: DoorSwing, segs = 18): Vec2[] {
  const { a0, sweep } = swingSweep(s);
  const out: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const a = a0 + (sweep * i) / segs;
    out.push({ x: s.hinge.x + Math.cos(a) * s.radius, y: s.hinge.y + Math.sin(a) * s.radius });
  }
  return out;
}

/** Stroke the swing on a canvas: the open leaf (unless `leaf` is false) and the
 *  arc, each point through `toS` (to screen). The caller sets the stroke. */
export function strokeSwing(ctx: CanvasRenderingContext2D, s: DoorSwing, toS: (p: Vec2) => Vec2, opts: { leaf?: boolean; segs?: number } = {}): void {
  if (opts.leaf !== false) {
    const h = toS(s.hinge), tip = toS(s.open);
    ctx.beginPath();
    ctx.moveTo(h.x, h.y);
    ctx.lineTo(tip.x, tip.y);
    ctx.stroke();
  }
  ctx.beginPath();
  swingArc(s, opts.segs).forEach((p, i) => { const q = toS(p); if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y); });
  ctx.stroke();
}

/** The swing's arc as one DXF ARC on layer DOORS (counter-clockwise, as DXF
 *  draws arcs); none for a leaf of no length. */
export function swingDxfArc(s: DoorSwing): DxfEntity | null {
  if (!(s.radius > 1e-6)) return null;
  const { a0, sweep } = swingSweep(s);
  const deg = (r: number): number => (r * 180) / Math.PI;
  const [from, to] = sweep >= 0 ? [a0, a0 + sweep] : [a0 + sweep, a0];
  return { kind: 'arc', c: s.hinge, r: s.radius, startDeg: deg(from), endDeg: deg(to), layer: 'DOORS' };
}
